import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "ProgramasFormacion";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const RegistrarProgramas = ({ actualizarProgramas }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [codPrograma, setCodPrograma] = useState("");
  const [nombrePrograma, setNombrePrograma] = useState("");
  const [duracionlectivaPrograma, setDuracionLectivaPrograma] = useState("");
  const [duracionproductivaPrograma, setDuracionProductivaPrograma] =
    useState("");
  const [estadoPrograma, setEstadoPrograma] = useState("");
  const [modalidadPrograma, setModalidadPrograma] = useState("");
  const [nivelPrograma, setNivelPrograma] = useState("");
  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => Boolean(id), [id]);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloProgramas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloProgramas = useMemo(() => getModuloProgramas(), []);

  const puedeCrear =
    moduloProgramas?.puedeCrear === true ||
    moduloProgramas?.crear === true ||
    moduloProgramas?.create === true;

  const puedeActualizar =
    moduloProgramas?.puedeActualizar === true ||
    moduloProgramas?.actualizar === true ||
    moduloProgramas?.editar === true ||
    moduloProgramas?.update === true;

  const puedeGuardarFormulario = isEdit ? puedeActualizar : puedeCrear;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const cleanData = () => {
    setCodPrograma("");
    setNombrePrograma("");
    setDuracionLectivaPrograma("");
    setDuracionProductivaPrograma("");
    setEstadoPrograma("");
    setModalidadPrograma("");
    setNivelPrograma("");
  };

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api/programas/listid/${id}`, authConfig());

      if (response.data.ok) {
        const programa = response.data.programa;

        setCodPrograma(programa.codPrograma || "");
        setNombrePrograma(programa.nombrePrograma || "");
        setDuracionLectivaPrograma(programa.duracionlectivaPrograma || "");
        setDuracionProductivaPrograma(programa.duracionproductivaPrograma || "");
        setEstadoPrograma(programa.estadoPrograma || "");
        setModalidadPrograma(programa.modalidadPrograma || "");
        setNivelPrograma(programa.nivelPrograma || "");
      }
    } catch (error) {
      console.error("Error al obtener datos del programa:", error.message);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar el programa", "error");
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const savePrograma = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar programas.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const newPrograma = {
        codPrograma,
        nombrePrograma,
        duracionlectivaPrograma,
        duracionproductivaPrograma,
        estadoPrograma,
        modalidadPrograma,
        nivelPrograma,
      };

      await axios.post("/api/programas/add", newPrograma, authConfig());

      cleanData();

      Swal.fire({
        icon: "success",
        title: "Programa registrado con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate("/institucional/programas/"));

      actualizarProgramas?.();
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      const msg = error.response?.data?.message || "Error al guardar el programa";
      Swal.fire("Error", msg, "error");
      console.error("Error en savePrograma:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePrograma = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar programas.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const updatedPrograma = {
        nombrePrograma,
        duracionlectivaPrograma,
        duracionproductivaPrograma,
        estadoPrograma,
        modalidadPrograma,
        nivelPrograma,
      };

      const { data } = await axios.put(
        `/api/programas/update/${id}`,
        updatedPrograma,
        authConfig()
      );

      Swal.fire({
        icon: "success",
        title: data.message || "Programa actualizado con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate("/institucional/programas/"));

      actualizarProgramas?.();
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      const msg =
        error.response?.data?.message || "Error al actualizar el programa";
      Swal.fire("Error", msg, "error");
      console.error("Error al actualizar el programa:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!codPrograma || !nombrePrograma.trim() || !estadoPrograma) {
      Swal.fire(
        "Atención",
        "Código, nombre y estado del programa son obligatorios.",
        "warning"
      );
      return;
    }

    isEdit ? await updatePrograma() : await savePrograma();
  };

  const handleCancel = () => {
    cleanData();
    navigate("/institucional/programas/");
  };

  return (
    <div >
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Programa" : "Registrar Programa"}
                </h5>

                <small className="text-secondary">
                  Completa la información del programa de formación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate("/institucional/programas/")}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-programa"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Código</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 12345"
                    value={codPrograma}
                    onChange={(e) => setCodPrograma(e.target.value)}
                    required
                    disabled={loading || isEdit}
                  />

                  {isEdit && (
                    <div className="form-text">
                      El código no se edita para mantener consistencia en registros.
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    Nombre del Programa
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Análisis y desarrollo de software"
                    value={nombrePrograma}
                    onChange={(e) => setNombrePrograma(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Estado</label>

                  <select
                    className="form-select"
                    value={estadoPrograma}
                    onChange={(e) => setEstadoPrograma(e.target.value)}
                    required
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Duración Lectiva (horas)
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Ej: 1440"
                    min="0"
                    value={duracionlectivaPrograma}
                    onChange={(e) => setDuracionLectivaPrograma(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Duración Productiva (horas)
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Ej: 864"
                    min="0"
                    value={duracionproductivaPrograma}
                    onChange={(e) =>
                      setDuracionProductivaPrograma(e.target.value)
                    }
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Modalidad</label>

                  <select
                    className="form-select"
                    value={modalidadPrograma}
                    onChange={(e) => setModalidadPrograma(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>
                    <option value="PRESENCIAL">PRESENCIAL</option>
                    <option value="VIRTUAL">VIRTUAL</option>
                    <option value="A DISTANCIA">A DISTANCIA</option>
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Nivel</label>

                  <select
                    className="form-select"
                    value={nivelPrograma}
                    onChange={(e) => setNivelPrograma(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>
                    <option value="TECNICO">TECNICO</option>
                    <option value="TECNOLOGO">TECNOLOGO</option>
                    <option value="AUXILIAR">AUXILIAR</option>
                    <option value="OPERARIO">OPERARIO</option>
                    <option value="CURSO ESPECIAL">CURSO ESPECIAL</option>
                    <option value="PROFUNDIZACION TECNICA">
                      PROFUNDIZACION TECNICA
                    </option>
                    <option value="EVENTO">EVENTO</option>
                  </select>
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    {puedeGuardarFormulario && (
                      <button
                        type="submit"
                        form="form-programa"
                        className="btn btn-dark"
                        disabled={loading}
                        title={isEdit ? "Actualizar" : "Guardar"}
                      >
                        <i
                          className={`fa-solid ${
                            loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                        ></i>
                        {loading
                          ? isEdit
                            ? "Actualizando…"
                            : "Guardando…"
                          : isEdit
                          ? "Actualizar"
                          : "Guardar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {!puedeGuardarFormulario && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fa-solid fa-lock me-2"></i>
                No tienes permiso para {isEdit ? "actualizar" : "crear"} programas.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarProgramas;